// ----------------------------------------------------------------------------
// Collection filters
// ----------------------------------------------------------------------------
//
// ----------------------------------------------------------------------------

// -------------------------------------
// Dependencies
// -------------------------------------
import '../../snippets/_containers/modal/modal';
import iamotaEventHandler from 'assets/scripts/helpers/event-handler';

// Styles
import './_collection-filters-sort.scss';


// -------------------------------------
// Variables
// -------------------------------------
// Selectors
const FILTER_WRAPPER          = '#FiltersWrapperDesktop';
const PRODUCT_COUNT           = '#ProductCount';
const PRODUCT_GRID_CONTAINER  = '#ProductGridContainer';
const PRODUCT_GRID            = '#ProductGrid';
const FILTER_DETAILS_ELEMENTS = '#FiltersForm .js--filter, #FiltersFormMobile .js--filter';
const FILTER_ELEMENT          = '.js--filter';
const ACTIVE_FILTER_MOBILE    = '.js--active-filters-mobile';
const ACTIVE_FILTER_DESKTOP   = '.js--active-filters-desktop';
const FILTERS_SELECTED        = '.js--filters__selected';
const FILTERS_SUMMARY         = '.js--filters__summary';
const SORT_SELECTOR           = '#SortBy';
const FILTERS_FORM_SELECTOR   = '#FiltersForm';

// Classes, IDs & Other
const FILTERS_FORM_ID      = 'FiltersForm';
const SORT_FORM_ID_DESKTOP = 'SortForm';
const SORT_FORM_ID_MOBILE  = 'SortFormMobile';
const LOADING_CLASS        = 'loading';
const OPEN_ATTR            = 'open';


// -------------------------------------
// Hooks
// -------------------------------------

// Class 'FiltersForm' to handle filter functionalities
class FiltersForm extends HTMLElement {
    constructor() {
        // Initializing event handlers and methods for filtering
        super();
        this.onActiveFilterClick = this.onActiveFilterClick.bind(this);

        // Debouncing the submit handler to avoid frequent submissions
        this.debouncedOnSubmit = debounce((event) => {
            this.onSubmitHandler(event);
        }, 500);

        // Adding event listeners for filter form input and keyup actions
        const filterForm = this.querySelector('form');
        filterForm.addEventListener('input', this.debouncedOnSubmit.bind(this));

        // Adding an event listener for the escape key to close details elements
        const filterWrapper = this.querySelector(FILTER_WRAPPER);
        if (filterWrapper) filterWrapper.addEventListener('keyup', onKeyUpEscape);

        // Adding a global click event listener to handle closing details elements
        document.addEventListener('click', this.onClickOutsideFilter.bind(this));
    }

    // Static method to set listeners for popstate events
    static setListeners() {
        const onHistoryChange = (event) => {
            const searchParams = event.state ? event.state.searchParams : FiltersForm.searchParamsInitial;
            if (searchParams === FiltersForm.searchParamsPrev) return;
            FiltersForm.renderPage(searchParams, null, false);
        };
        window.addEventListener('popstate', onHistoryChange);
    }

    // Static method to render the page with updated filter parameters
    static renderPage(searchParams, event, updateURLHash = true) {
        // Implementation details for rendering different sections of the page
        FiltersForm.searchParamsPrev = searchParams;
        const sections = FiltersForm.getSections();
        document.querySelector(PRODUCT_GRID_CONTAINER).querySelector('.collection').classList.add(LOADING_CLASS);

        sections.forEach((section) => {
            const url = `${window.location.pathname}?section_id=${section.section}&${searchParams}`;
            const filterDataUrl = (element) => element.url === url;

            FiltersForm.filterData.some(filterDataUrl)
                ? FiltersForm.renderSectionFromCache(filterDataUrl, event)
                : FiltersForm.renderSectionFromFetch(url, event);
        });

        // Updating the URL hash if required
        if (updateURLHash) FiltersForm.updateURLHash(searchParams);
    }

    static renderSectionFromFetch(url, event) {
        fetch(url)
            .then((response) => response.text())
            .then((responseText) => {
                const html = responseText;
                FiltersForm.filterData = [...FiltersForm.filterData, { html, url }];
                FiltersForm.renderFilters(html, event);
                FiltersForm.renderProductGridContainer(html);
                FiltersForm.renderProductCount(html);
                FiltersForm.renderNoResultsMessage(html);
            });
    }

    static renderSectionFromCache(filterDataUrl, event) {
        const html = FiltersForm.filterData.find(filterDataUrl).html;
        FiltersForm.renderFilters(html, event);
        FiltersForm.renderProductGridContainer(html);
        FiltersForm.renderProductCount(html);
        FiltersForm.renderNoResultsMessage(html);
    }

    static renderProductGridContainer(html) {
        document.querySelector(PRODUCT_GRID_CONTAINER).innerHTML = new DOMParser()
            .parseFromString(html, 'text/html')
            .querySelector(PRODUCT_GRID_CONTAINER).innerHTML;
    }

    static renderProductCount(html) {
        const countContainers = document.querySelectorAll(PRODUCT_COUNT);
        const parsedHTML = new DOMParser().parseFromString(html, 'text/html');
        const counts = parsedHTML.querySelectorAll(PRODUCT_COUNT);

        if (countContainers) {
            countContainers.forEach((container, index) => {
                if (counts[index]) {
                    // Update innerHTML
                    container.innerHTML = counts[index].innerHTML;

                    // Update data-product-count attribute
                    const dataProductCount = counts[index].getAttribute('data-product-count');
                    container.setAttribute('data-product-count', dataProductCount);

                    container.classList.remove(LOADING_CLASS);
                }
            });
        }
    }

    static renderNoResultsMessage() {
        const countContainers = document.querySelector(PRODUCT_COUNT);
        
        if (!countContainers) return false;
        
        const productCountValue = countContainers.getAttribute('data-product-count');

        if( productCountValue ) {
            if(productCountValue === '0') {
                document.querySelector(PRODUCT_GRID).innerHTML = '<p class="no-results-text">' + window.translations.noResultsFound + '</p>';
            }
        }
    }

    static renderFilters(html, event) {
        const parsedHTML = new DOMParser().parseFromString(html, 'text/html');

        const filterDetailsElements = parsedHTML.querySelectorAll(FILTER_DETAILS_ELEMENTS);
        const matchesIndex = (element) => {
            const jsFilter = event ? event.target.closest(FILTER_ELEMENT) : undefined;
            return jsFilter ? element.dataset.index === jsFilter.dataset.index : false;
        };
        const filtersToRender = Array.from(filterDetailsElements).filter((element) => !matchesIndex(element));
        const countsToRender = Array.from(filterDetailsElements).find(matchesIndex);

        filtersToRender.forEach((element) => {
            document.querySelector(`${FILTER_ELEMENT}[data-index="${element.dataset.index}"]`).className = element.classList;
            document.querySelector(`${FILTER_ELEMENT}[data-index="${element.dataset.index}"]`).innerHTML = element.innerHTML;
        });

        FiltersForm.renderActiveFilters(parsedHTML);

        if (countsToRender) FiltersForm.renderCounts(countsToRender, event.target.closest(FILTER_ELEMENT));
    }

    static renderActiveFilters(html) {
        const activeFilterElementSelectors = [ACTIVE_FILTER_MOBILE, ACTIVE_FILTER_DESKTOP];

        activeFilterElementSelectors.forEach((selector) => {
            const activeFiltersElement = html.querySelector(selector);
            if (!activeFiltersElement) return;
            document.querySelector(selector).innerHTML = activeFiltersElement.innerHTML;
        });
    }

    static renderCounts(source, target) {
        const targetElement = target.querySelector(FILTERS_SELECTED);
        const sourceElement = source.querySelector(FILTERS_SELECTED);

        const targetElementAccessibility = target.querySelector(FILTERS_SUMMARY);
        const sourceElementAccessibility = source.querySelector(FILTERS_SUMMARY);

        if (sourceElement && targetElement) {
            target.querySelector(FILTERS_SELECTED).outerHTML = source.querySelector(FILTERS_SELECTED).outerHTML;
        }

        if (targetElementAccessibility && sourceElementAccessibility) {
            target.querySelector(FILTERS_SUMMARY).outerHTML = source.querySelector(FILTERS_SUMMARY).outerHTML;
        }
    }

    static updateURLHash(searchParams) {
        history.pushState({ searchParams }, '', `${window.location.pathname}${searchParams && '?'.concat(searchParams)}`);

        iamotaEventHandler.triggerEvent('filterPramsUpdated', {filterParams: searchParams});
    }

    static getSections() {
        return [
            {
                section: document.querySelector(PRODUCT_GRID).dataset.id,
            },
        ];
    }

    // Handling event changes for sorting selectors
    connectedCallback() {
        const sortSelectors = document.querySelectorAll(SORT_SELECTOR);

        if (sortSelectors.length > 0) {
            sortSelectors.forEach((sortSelector) => {
                sortSelector.addEventListener('change', this.handleSelectorChange.bind(this));
            });
        }
    }

    // Event handler for sorting selector changes
    handleSelectorChange(event) {
        const sortSelectors = document.querySelectorAll(SORT_SELECTOR);
        const selectedValue = event.target.value;

        // Remove the event listener from the current element
        event.target.removeEventListener('change', this.handleSelectorChange);
        sortSelectors.forEach((otherSelector) => {
            if (otherSelector !== event.target) {
                otherSelector.value = selectedValue;
            }
        });

        event.target.addEventListener('change', this.handleSelectorChange.bind(this));
    }

    // Method to create search parameters from form data
    createSearchParams(form) {
        const formData = new FormData(form);
        return new URLSearchParams(formData).toString();
    }

    // Event handler for form submissions
    onSubmitForm(searchParams, event) {
        FiltersForm.renderPage(searchParams, event);
    }

    // Event handler for form input changes
    onSubmitHandler(event) {
        event.preventDefault();
        const sortFilterForms = document.querySelectorAll('filters-form form');

        const forms = [];
        const isMobile = event.target.closest('form').id === SORT_FORM_ID_MOBILE;

        sortFilterForms.forEach((form) => {
            if (!isMobile) {
                if ( form.id === SORT_FORM_ID_DESKTOP || form.id === FILTERS_FORM_ID ) {
                    forms.push(this.createSearchParams(form));
                }
            } else if (form.id === SORT_FORM_ID_MOBILE || form.id === FILTERS_FORM_ID ) {
                forms.push(this.createSearchParams(form));
            }
        });
        this.onSubmitForm(forms.join('&'), event);
    }

    // Event handler for active filter clicks
    onActiveFilterClick(event) {
        event.preventDefault();
        const url =
            event.currentTarget.href.indexOf('?') == -1
                ? ''
                : event.currentTarget.href.slice(event.currentTarget.href.indexOf('?') + 1);
        FiltersForm.renderPage(url);
    }

    // Event handler to close filter details when clicking outside
    onClickOutsideFilter(event) {
        const filterDetailsElements = document.querySelectorAll(FILTER_DETAILS_ELEMENTS);
        const isOutsideFilter = !event.target.closest(FILTER_ELEMENT);

        if (isOutsideFilter) {
            filterDetailsElements.forEach((element) => {
                element.removeAttribute(OPEN_ATTR);
            });
        }
    }
}

FiltersForm.filterData = [];
FiltersForm.searchParamsInitial = window.location.search.slice(1);
FiltersForm.searchParamsPrev = window.location.search.slice(1);
customElements.define('filters-form', FiltersForm);
FiltersForm.setListeners();

// Reset the price range to the minimum and maximum
class PriceRange extends HTMLElement {
    constructor() {
        super();
        this.querySelectorAll('input').forEach((element) =>
            element.addEventListener('change', this.onRangeChange.bind(this))
        );
        this.setMinAndMaxValues();
    }

    onRangeChange(event) {
        this.adjustToValidValues(event.currentTarget);
        this.setMinAndMaxValues();
    }

    setMinAndMaxValues() {
        const inputs = this.querySelectorAll('input');
        const minInput = inputs[0];
        const maxInput = inputs[1];
        if (maxInput.value) minInput.setAttribute('max', maxInput.value);
        if (minInput.value) maxInput.setAttribute('min', minInput.value);
        if (minInput.value === '') maxInput.setAttribute('min', 0);
        if (maxInput.value === '') minInput.setAttribute('max', maxInput.getAttribute('max'));
    }

    adjustToValidValues(input) {
        const value = Number(input.value);
        const min = Number(input.getAttribute('min'));
        const max = Number(input.getAttribute('max'));

        if (value < min) input.value = min;
        if (value > max) input.value = max;
    }
}
customElements.define('price-range', PriceRange);

class FilterRemove extends HTMLElement {
    constructor() {
        super();
        const filterLink = this.querySelector('a');
        filterLink.setAttribute('role', 'button');
        filterLink.addEventListener('click', this.closeFilter.bind(this));
        filterLink.addEventListener('keyup', (event) => {
            event.preventDefault();
            if (event.code.toUpperCase() === 'SPACE') this.closeFilter(event);
        });
    }

    closeFilter(event) {
        event.preventDefault();
        const form = this.closest('filters-form') || document.querySelector('filters-form');
        form.onActiveFilterClick(event);
    }
}
customElements.define('filter-remove', FilterRemove);

// -------------------------------------
// Helpers
// -------------------------------------

// Limit the frequency of a function's execution
function debounce(fn, wait) {
    let t;
    return (...args) => {
        clearTimeout(t);
        t = setTimeout(() => fn.apply(this, args), wait);
    };
}

// Function to handle keyup 'Escape' event to close details element
function onKeyUpEscape(event) {
    if (event.code.toUpperCase() !== 'ESCAPE') return;

    const openDetailsElement = event.target.closest('details[open]');
    if (!openDetailsElement) return;

    const summaryElement = openDetailsElement.querySelector('summary');
    openDetailsElement.removeAttribute(OPEN_ATTR);
    summaryElement.setAttribute('aria-expanded', false);
    summaryElement.focus();
}

function toggleFilters(action) {
    const filtersForm = document.querySelector(FILTERS_FORM_SELECTOR);
    const filters = filtersForm ? filtersForm.querySelectorAll(FILTER_ELEMENT) : [];

    if (!filtersForm || filters.length === 0) return;

    filters.forEach(function (filter) {
        if (filter && action === 'close') {
            filter.removeAttribute(OPEN_ATTR);
        }
    });
}

function toggleDropdowns() {
    const dropdown = document.querySelectorAll('select');
    
    dropdown.forEach((el) => {
        el.addEventListener('focus', function() {
            el.classList.add('js--opened');
        });
    
        el.addEventListener('blur', function() {
            el.classList.remove('js--opened');
        });
        
        el.addEventListener('change', function() {
            el.classList.remove('js--opened');
        });
    });

    document.addEventListener('DOMContentLoaded', () => {
        // Select all details elements with the class js--filter
        const filterDetailsElements = document.querySelectorAll('details.js--filter');
    
        filterDetailsElements.forEach(details => {
            // Ensure all filters start closed
            details.removeAttribute('open');

            details.addEventListener('toggle', () => {
                // Only proceed if the details element is opened
                if (details.hasAttribute('open')) {
                    // Close all other details elements
                    filterDetailsElements.forEach(otherDetails => {
                        if (otherDetails !== details) {
                            otherDetails.removeAttribute('open');
                        }
                    });
                }
            });
        });
    
    });
    
}


// Function to handle click events on summary elements within details
function handleFilterClick() {

    // // Close all other details elements except the clicked one
    // closeAllFiltersExcept(detailsElement);

    // // Toggle 'open' attribute on the clicked details element
    // detailsElement.toggleAttribute(OPEN_ATTR);
}

// // Function to close all details elements except the clicked one
// function closeAllFiltersExcept(clickedFilter) {
//     const filterDetailsElements = document.querySelectorAll(FILTER_DETAILS_ELEMENTS);
//     filterDetailsElements.forEach(filter => {
//         if (filter !== clickedFilter) {
//             filter.removeAttribute(OPEN_ATTR);
//         }
//     });
// }

// Add click event listeners to all summary elements within filter details
const summaryElements = document.querySelectorAll(`${FILTER_DETAILS_ELEMENTS} summary`);
summaryElements.forEach(summary => {
    summary.addEventListener('click', handleFilterClick);
});


// Add click event listeners to all filter elements
const filterElements = document.querySelectorAll(FILTERS_SUMMARY);
filterElements.forEach(filter => {
    filter.addEventListener('click', handleFilterClick);
});

// Function to initialize filter event listeners
function initializeFilterEventListeners() {
    const filterElements = document.querySelectorAll(FILTERS_SUMMARY);
    filterElements.forEach(filter => {
        filter.addEventListener('click', handleFilterClick);
    });
}


function handleFiltersByViewport() {
    const action = 'close';

    toggleDropdowns();
    toggleFilters(action);
    initializeFilterEventListeners();
}




// -------------------------------------
// Hooks
// -------------------------------------

document.addEventListener('DOMContentLoaded', handleFiltersByViewport);

window.addEventListener('resize', handleFiltersByViewport);
